// EducationConfiguration.tsx
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Stack } from '@sureco/design-system'
import { Select, message } from 'antd'
import ContentSection from 'layouts/ContentSection/ContentSection'

import useEducationConfiguration, {
  EmployeeHandbookDataDto,
} from '../hooks/useEducationConfiguration'
import {
  useEmployeeHandbookUpload,
  EmployeeHandbookLanguage,
} from '../hooks/useEmployeeHandbookUpload'
import { useHealthplanChecklistUpload } from '../hooks/useHealthplanChecklistUpload'

import BrainSharkSection from './BrainSharkSection'
import HandbookSection from './HandbookSection'
import LanguageModal from './LanguageModal'
import ConflictModal from './ConflictModal'
import DeleteHandbookModal from './DeleteModal'

type ApiLanguage = EmployeeHandbookLanguage
type DocumentKind = 'handbook' | 'checklist'

export default function EducationConfiguration({ id }: { id: string }) {
  const {
    isLoading,
    getBrainSharkUrlForYear,
    employeeHandbooks,
    healthplanChecklists,
    availableYears,
    saveBrainSharkUrl,
    refetch,
    deleteHandbook,
    deleteHealthplanChecklist,
    isDeleting,
    isDeletingChecklist,
  } = useEducationConfiguration(id)

  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState<number>(
    availableYears.length ? availableYears[0] : currentYear
  )

  const handbooksForYear = useMemo(
    () => employeeHandbooks.filter((hb) => hb.year === year),
    [employeeHandbooks, year]
  )

  const checklistsForYear = useMemo(
    () => healthplanChecklists.filter((cl) => cl.year === year),
    [healthplanChecklists, year]
  )

  const handbookFileInputRef = useRef<HTMLInputElement | null>(null)
  const checklistFileInputRef = useRef<HTMLInputElement | null>(null)

  const [pendingKind, setPendingKind] = useState<DocumentKind | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [selectedLanguage, setSelectedLanguage] =
    useState<ApiLanguage>('english')
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false)

  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false)
  const [conflictExisting, setConflictExisting] =
    useState<EmployeeHandbookDataDto | null>(null)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteKind, setDeleteKind] = useState<DocumentKind | null>(null)
  const [fileToDelete, setFileToDelete] =
    useState<EmployeeHandbookDataDto | null>(null)

  const effectiveApiLanguage: ApiLanguage =
    selectedLanguage === 'spanish' ? 'spanish' : 'english'

  const {
    handleUpload: handleHandbookUpload,
    isUploading: isHandbookUploading,
    state: handbookUploadState,
  } = useEmployeeHandbookUpload({
    clientPublicKey: id,
    year,
    onSaved: () => {
      refetch()
      setIsLanguageModalOpen(false)
      setPendingFile(null)
      setPendingKind(null)
    },
  })

  const {
    handleUpload: handleChecklistUpload,
    isUploading: isChecklistUploading,
    state: checklistUploadState,
  } = useHealthplanChecklistUpload({
    clientPublicKey: id,
    year,
    onSaved: () => {
      refetch()
      setIsLanguageModalOpen(false)
      setPendingFile(null)
      setPendingKind(null)
    },
  })

  const isUploading = isHandbookUploading || isChecklistUploading

  const onChooseHandbookFile = useCallback(() => {
    setPendingKind('handbook')
    handbookFileInputRef.current?.click()
  }, [])

  const onChooseChecklistFile = useCallback(() => {
    setPendingKind('checklist')
    checklistFileInputRef.current?.click()
  }, [])

  const onHandbookFileChange = useCallback((e) => {
    const file = e.target.files?.[0] ?? null
    if (!file) return
    setPendingKind('handbook')
    setPendingFile(file)
    setIsLanguageModalOpen(true)
  }, [])

  const onChecklistFileChange = useCallback((e) => {
    const file = e.target.files?.[0] ?? null
    if (!file) return
    setPendingKind('checklist')
    setPendingFile(file)
    setIsLanguageModalOpen(true)
  }, [])

  const uploadNow = useCallback(async () => {
    if (!pendingFile || !pendingKind) return
    if (pendingKind === 'handbook') {
      await handleHandbookUpload(pendingFile, effectiveApiLanguage)
      return
    }
    await handleChecklistUpload(pendingFile, effectiveApiLanguage)
  }, [
    pendingFile,
    pendingKind,
    effectiveApiLanguage,
    handleHandbookUpload,
    handleChecklistUpload,
  ])

  const handleSaveAndAdd = useCallback(() => {
    if (!pendingFile || !pendingKind) {
      message.error('No file selected.')
      return
    }

    const docsForYear =
      pendingKind === 'handbook' ? handbooksForYear : checklistsForYear
    const exists = docsForYear.find(
      (doc) => doc.language === effectiveApiLanguage
    )

    if (exists) {
      setConflictExisting(exists)
      setIsConflictModalOpen(true)
      return
    }

    uploadNow()
  }, [
    pendingFile,
    pendingKind,
    handbooksForYear,
    checklistsForYear,
    effectiveApiLanguage,
    uploadNow,
  ])

  const handleCancelLanguage = useCallback(() => {
    setIsLanguageModalOpen(false)
    setPendingFile(null)
    setPendingKind(null)
    if (handbookFileInputRef.current) handbookFileInputRef.current.value = ''
    if (checklistFileInputRef.current) checklistFileInputRef.current.value = ''
  }, [])

  const handleDeleteHandbookClick = useCallback(
    (hb: EmployeeHandbookDataDto) => {
      setDeleteKind('handbook')
      setFileToDelete(hb)
      setIsDeleteModalOpen(true)
    },
    []
  )

  const handleDeleteChecklistClick = useCallback(
    (doc: EmployeeHandbookDataDto) => {
      setDeleteKind('checklist')
      setFileToDelete(doc)
      setIsDeleteModalOpen(true)
    },
    []
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!fileToDelete || !deleteKind) return
    if (deleteKind === 'handbook') {
      await deleteHandbook(fileToDelete)
    } else {
      await deleteHealthplanChecklist(fileToDelete)
    }
    setIsDeleteModalOpen(false)
    setFileToDelete(null)
    setDeleteKind(null)
  }, [fileToDelete, deleteKind, deleteHandbook, deleteHealthplanChecklist])

  return (
    <ContentSection
      loading={isLoading}
      title="Education"
      actions={
        <Select
          value={year}
          onChange={setYear}
          style={{ width: 120 }}
          options={availableYears.map((y) => ({
            label: y.toString(),
            value: y,
          }))}
        />
      }
    >
      <Stack space={40}>
        <Stack direction="row" space={40}>
          <BrainSharkSection
            year={year}
            getBrainSharkUrlForYear={getBrainSharkUrlForYear}
            saveBrainSharkUrl={saveBrainSharkUrl}
          />

          <HandbookSection
            handbooks={handbooksForYear}
            onChooseFile={onChooseHandbookFile}
            onDelete={handleDeleteHandbookClick}
            isUploading={isHandbookUploading}
            uploadState={handbookUploadState}
            fileInputRef={handbookFileInputRef}
            onFileChange={onHandbookFileChange}
          />
        </Stack>

        <HandbookSection
          title="Healthplan Checklist"
          emptyMessage="Upload a PDF checklist for employees to download on Shop for Health."
          handbooks={checklistsForYear}
          onChooseFile={onChooseChecklistFile}
          onDelete={handleDeleteChecklistClick}
          isUploading={isChecklistUploading}
          uploadState={checklistUploadState}
          fileInputRef={checklistFileInputRef}
          onFileChange={onChecklistFileChange}
        />
      </Stack>

      <LanguageModal
        open={isLanguageModalOpen}
        pendingFile={pendingFile}
        selectedLanguage={selectedLanguage}
        setSelectedLanguage={setSelectedLanguage}
        onCancel={handleCancelLanguage}
        onSave={handleSaveAndAdd}
        isUploading={isUploading}
      />

      <ConflictModal
        open={isConflictModalOpen}
        existing={conflictExisting}
        onReplace={() => {
          setIsConflictModalOpen(false)
          uploadNow()
        }}
        onCancel={() => {
          setIsConflictModalOpen(false)
          handleCancelLanguage()
        }}
        isUploading={isUploading}
      />
      <DeleteHandbookModal
        open={isDeleteModalOpen}
        handbook={fileToDelete}
        onDelete={handleConfirmDelete}
        onCancel={() => {
          setIsDeleteModalOpen(false)
          setFileToDelete(null)
          setDeleteKind(null)
        }}
        isDeleting={isDeleting || isDeletingChecklist}
      />
    </ContentSection>
  )
}
